import { useLazyQuery } from '@apollo/client';
import { ClickAwayListener, Popper, TextField } from '@mui/material';
import { gql } from '__generated__';
import { FC, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';

import debounce from 'utils/debounce';

import theme from './theme.module.scss';

type User = {
  id: string;
  fullName: string;
};

const SEARCH_QUERY = gql(`
  query SearchUsers($filters: UserFilterInputType, $limit: Int) {
    users(filters: $filters, limit: $limit) {
      nodes {
        id
        fullName
      }
      pageInfo {
        cursor
        totalCount
      }
    }
  }
`);

const SearchUsers: FC<{
  onChange: (user: User) => void;
  value?: User;
  label?: string;
}> = ({ value, onChange, label = '' }) => {
  const [currentValue, setCurrentValue] = useState<User | null>(value || null);
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };
  const [getUsers, { loading, error, data, refetch, variables }] = useLazyQuery(SEARCH_QUERY, {
    fetchPolicy: 'network-only',
  });

  const ref = useRef<HTMLDivElement | null>(null);

  const performSearch = searchTerm => {
    if (!data) {
      getUsers({
        variables: {
          filters: {
            departmentId,
            text: searchTerm.trim(),
          },
          limit: 4,
        },
      });
      return;
    }
    refetch({
      filters: {
        departmentId,
        text: searchTerm.trim(),
      },
    });
  };

  function results() {
    if (loading) return <LoadingIndicator />;

    if (error) return <ErrorMessage error={error} refetch={refetch} />;

    if (!data || !data.users || variables?.filters?.text === '') return null;

    const users = data.users.nodes;

    if (users.length === 0) return <p>No results found. Please change your search.</p>;

    return (
      <>
        {users.map(user => (
          <div
            key={user.id}
            className={theme.user}
            onClick={() => {
              setCurrentValue(user);
              onChange(user as User);
            }}
          >
            <span className={theme.name}>{user.fullName}</span>
          </div>
        ))}
      </>
    );
  }

  if (currentValue) {
    return (
      <>
        <p>{currentValue.fullName}</p>
        <Button onClick={() => setCurrentValue(null)}>Clear</Button>
      </>
    );
  }

  return (
    <div className={theme.container}>
      <ClickAwayListener onClickAway={() => performSearch('')}>
        <div className={theme.inputContainer}>
          <TextField
            ref={ref}
            className={theme.input}
            fullWidth
            size="small"
            label={label}
            onBlur={e => (e.target.value = '')}
            onChange={debounce(e => performSearch(e.target.value), 500)}
            placeholder="Find by name"
          />
          <Popper
            open={true}
            style={{ width: ref.current?.offsetWidth, zIndex: 10000 }}
            anchorEl={ref.current}
            placement="bottom-start"
            onClick={e => e.preventDefault()}
          >
            <div className={theme.resultsContainer}>{results()}</div>
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default SearchUsers;
